import { useEffect, useLayoutEffect, useMemo, useState, type SyntheticEvent } from 'react';

import { useNavigate } from 'react-router-dom';

import { motion } from 'framer-motion';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useListCustomProjectsQuery } from '@/api/customProjects/customProjectsApi';
import { useListLeadProjectsQuery } from '@/api/lead/leadApi';
import NoResultsFound from '@/assets/NoResultsFound.svg';
import SampleProject1 from '@/assets/SampleProject1.svg';
import SampleProject10 from '@/assets/SampleProject10.svg';
import SampleProject11 from '@/assets/SampleProject11.svg';
import SampleProject2 from '@/assets/SampleProject2.svg';
import SampleProject3 from '@/assets/SampleProject3.svg';
import SampleProject4 from '@/assets/SampleProject4.svg';
import SampleProject5 from '@/assets/SampleProject5.svg';
import SampleProject6 from '@/assets/SampleProject6.svg';
import SampleProject7 from '@/assets/SampleProject7.svg';
import SampleProject8 from '@/assets/SampleProject8.svg';
import SampleProject9 from '@/assets/SampleProject9.svg';
import { CreateNewProjectDialog } from '@/pages/Home/components/CreateNewProjectDialog';
import { CustomProjectCard } from '@/pages/Home/components/CustomProjectCard';
import { HomePaginationBar } from '@/pages/Home/components/HomePaginationBar';
import { WorkbenchProjectCard } from '@/pages/Home/components/WorkbenchProjectCard';
import { buildHeaderNavigationState } from '@/utils/headerContext';
import { useDebounce } from '@/utils/useDebounce';

type ProjectTab = 'custom' | 'workbench';
const PROJECT_CARD_STAGGER_SEC = 0.09;
const projectCardEnterEase = [0.25, 0.46, 0.45, 0.94] as const;
const emptyStateCardTransition =
  'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease';

const sampleProjectImages = [
  SampleProject1,
  SampleProject2,
  SampleProject3,
  SampleProject4,
  SampleProject5,
  SampleProject6,
  SampleProject7,
  SampleProject8,
  SampleProject9,
  SampleProject10,
  SampleProject11,
];

function getStableImageIndex(seed: string, total: number): number {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return hash % total;
}

function getProjectPreviewImage(seedParts: Array<string | number | null | undefined>): string {
  const seed = seedParts.filter(Boolean).join('-');
  return sampleProjectImages[
    getStableImageIndex(seed || 'default-project', sampleProjectImages.length)
  ];
}

function getWorkbenchProjectDisplayName(companyName: string, projectCode: number): string {
  const normalizedCompanyName = companyName.trim();
  if (normalizedCompanyName) return normalizedCompanyName;
  return `Project ${projectCode}`;
}

function cardMotionTransition(index: number, cardsEnter: boolean) {
  if (!cardsEnter) return { duration: 0 };

  return {
    duration: 0.38,
    ease: projectCardEnterEase,
    delay: index * PROJECT_CARD_STAGGER_SEC,
  };
}

export default function HomePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ProjectTab>('custom');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(8);

  const { data, isLoading } = useListCustomProjectsQuery(
    { page, limit, search: debouncedSearch },
    { skip: tab !== 'custom' },
  );

  const { data: leadData, isLoading: leadLoading } = useListLeadProjectsQuery(
    { statusFilter: 'InProgress', page, limit, search: debouncedSearch },
    { skip: tab !== 'workbench' },
  );

  const isInitialLoad = tab === 'custom' && isLoading && data == null;
  const isWorkbenchInitialLoad = tab === 'workbench' && leadLoading && leadData == null;

  const filteredProjects = useMemo(() => data?.projects ?? [], [data]);
  const workbenchProjects = useMemo(() => leadData?.projects ?? [], [leadData]);

  const customTotalCount = data?.count ?? 0;
  const workbenchTotalCount = leadData?.totalCount ?? 0;

  useEffect(() => {
    setPage(1);
  }, [tab, debouncedSearch, limit]);

  useEffect(() => {
    const total = tab === 'custom' ? customTotalCount : workbenchTotalCount;
    const maxPage = Math.max(1, Math.ceil(total / limit) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [tab, customTotalCount, workbenchTotalCount, limit, page]);

  const handleTabChange = (_: SyntheticEvent, value: ProjectTab) => {
    setSearch('');
    setTab(value);
  };

  const handleLimitChange = (next: number) => {
    setLimit(next);
  };

  const hasCustomProjects = filteredProjects.length > 0;
  const showCustomEmptyState = !isInitialLoad && !hasCustomProjects && !search.trim();
  const useGridLayout = !(tab === 'custom' && showCustomEmptyState);

  const customCardsSignature = useMemo(
    () => ['new-project-card', ...filteredProjects.map(project => String(project.id))].join('|'),
    [filteredProjects],
  );
  const workbenchCardsSignature = useMemo(
    () => workbenchProjects.map(project => String(project.projectId)).join('|'),
    [workbenchProjects],
  );
  const activeCardsSignature = tab === 'custom' ? customCardsSignature : workbenchCardsSignature;
  const [cardsEnter, setCardsEnter] = useState(false);

  useLayoutEffect(() => {
    if (
      (tab === 'custom' && (showCustomEmptyState || isInitialLoad)) ||
      (tab === 'workbench' && isWorkbenchInitialLoad)
    ) {
      setCardsEnter(false);
      return undefined;
    }

    setCardsEnter(false);
    let cancelled = false;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (!cancelled) setCardsEnter(true);
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeCardsSignature, isInitialLoad, isWorkbenchInitialLoad, showCustomEmptyState, tab]);

  const isSearching = Boolean(debouncedSearch.trim());
  const noCustomResults = isSearching && filteredProjects.length === 0 && !isLoading;
  const noWorkbenchResults = isSearching && workbenchProjects.length === 0 && !leadLoading;

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 'calc(100vh - 52px)',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(-0.03deg, #f7f8fa 0%, #ffffff 45%, #ececec 100%)',
        px: { xs: 2, sm: 3, md: '52px' },
        py: { xs: 3, md: 4 },
        pb: 6,
      }}
    >
      <Stack spacing={3} sx={{ flex: 1 }}>
        <Stack spacing={3} sx={{ flex: 1 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Tabs
              value={tab}
              onChange={handleTabChange}
              sx={{
                minHeight: 40,
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-flexContainer': { gap: 0 },
                border: '1px solid rgba(120, 128, 139, 0.46)',
                background: 'rgba(149, 150, 159, 0.14)',
                padding: '5px',
                borderRadius: '6px',
              }}
            >
              <Tab
                disableRipple
                value="custom"
                label="Custom Projects"
                sx={{
                  minHeight: 26,
                  padding: '8px',
                  minWidth: 0,
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  color: tab === 'custom' ? '#1E2A39' : '#718096',
                  bgcolor: tab === 'custom' ? '#fff' : 'transparent',
                  border: '1px solid',
                  borderColor: tab === 'custom' ? '#ececec' : 'transparent',
                  boxShadow:
                    tab === 'custom'
                      ? '2px 2px 4px rgba(43,43,43,0.08), 0 1px 2px rgba(0,0,0,0.06)'
                      : 'none',
                  mr: 1,
                  '&.Mui-selected': { color: '#1e2a39' },
                }}
              />
              <Tab
                value="workbench"
                label="Workbench Projects"
                sx={{
                  minHeight: 26,
                  padding: '8px',
                  borderRadius: '5px',
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#718096',
                  border: '1px solid transparent',

                  '&.Mui-selected': {
                    color: '#1e2a39',
                    bgcolor: '#fff',
                    borderColor: '#ececec',
                    boxShadow: '2px 2px 4px rgba(43,43,43,0.08), 0 1px 2px rgba(0,0,0,0.06)',
                    fontWeight: 600,
                  },
                }}
              />
            </Tabs>

            <TextField
              size="small"
              placeholder="Search by project name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#718096', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', md: 360 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: '10px',
                  fontSize: 13,
                  '& fieldset': { borderColor: '#ececec' },
                  '&:hover fieldset': { borderColor: '#d0d0d0' },
                },
              }}
            />
          </Stack>

          <Box
            sx={{
              display: useGridLayout ? 'grid' : 'block',
              gridTemplateColumns: useGridLayout
                ? {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(4, 1fr)',
                  }
                : undefined,
              rowGap: useGridLayout ? '37px' : undefined,
              columnGap: useGridLayout ? 3 : undefined,
              maxHeight: 'calc(100vh - 240px)',
              overflowY: 'auto',
              padding: '10px',
            }}
          >
            {tab === 'custom' ? (
              <>
                {!showCustomEmptyState && !noCustomResults ? (
                  <Box
                    component={motion.div}
                    role="button"
                    tabIndex={0}
                    onClick={() => setCreateProjectOpen(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCreateProjectOpen(true);
                      }
                    }}
                    initial={false}
                    animate={{
                      opacity: cardsEnter ? 1 : 0,
                      y: cardsEnter ? 0 : 14,
                    }}
                    transition={cardMotionTransition(0, cardsEnter)}
                    sx={{
                      height: '240px',
                      borderRadius: '10px',
                      border: '1px solid #ECECEC',
                      background:
                        'linear-gradient(0deg, #FFF 0%, #FFF 100%), linear-gradient(180deg, rgba(246, 246, 246, 0.50) 0%, rgba(251, 251, 251, 0.50) 58.65%, rgba(255, 255, 255, 0.50) 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, border-color 0.15s',
                      '&:hover': {
                        bgcolor: '#fff',
                        borderColor: 'rgba(30, 42, 57, 0.35)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        border: '1px solid rgba(120, 128, 139, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18, color: '#1e2a39' }} />
                    </Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1e2a39' }}>
                      New Project
                    </Typography>
                  </Box>
                ) : null}
                {isInitialLoad ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 120,
                    }}
                  >
                    <CircularProgress size={28} sx={{ color: '#1e2a39' }} />
                  </Box>
                ) : (
                  filteredProjects.map(project => (
                    <Box
                      key={project.id}
                      component={motion.div}
                      initial={false}
                      animate={{
                        opacity: cardsEnter ? 1 : 0,
                        y: cardsEnter ? 0 : 14,
                      }}
                      transition={cardMotionTransition(
                        filteredProjects.indexOf(project) + 1,
                        cardsEnter,
                      )}
                    >
                      <CustomProjectCard
                        imageSrc={getProjectPreviewImage([
                          project.id,
                          project.projectName,
                          project.clientName,
                        ])}
                        project={project}
                        onEnterWorkspace={() =>
                          navigate(
                            {
                              pathname: `/projects/${project.id}/ffne`,
                              search: new URLSearchParams({ type: tab }).toString(),
                            },
                            {
                              state: buildHeaderNavigationState({
                                projectId: project.id,
                                projectName: project.projectName,
                              }),
                            },
                          )
                        }
                      />
                    </Box>
                  ))
                )}
                {showCustomEmptyState ? (
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setCreateProjectOpen(true)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCreateProjectOpen(true);
                      }
                    }}
                    sx={{
                      minHeight: { xs: 280, md: 320 },
                      borderRadius: '14px',
                      border: '1px solid #E5E7EB',
                      background: '#FFFFFF',
                      boxShadow: '0px 3px 10px rgba(16, 24, 40, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 3,
                      cursor: 'pointer',
                      transition: emptyStateCardTransition,
                      '&:hover': {
                        borderColor: 'rgba(30, 42, 57, 0.25)',
                        boxShadow: '0px 8px 24px rgba(16, 24, 40, 0.08)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Stack
                      alignItems="center"
                      spacing={2}
                      sx={{ textAlign: 'center', maxWidth: 460 }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '50%',
                          border: '1px solid rgba(120, 128, 139, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#FFFFFF',
                        }}
                      >
                        <AddIcon sx={{ fontSize: 22, color: '#1E2A39' }} />
                      </Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#1E2A39' }}>
                        Start by creating your first project to begin building proposals.
                      </Typography>
                    </Stack>
                  </Box>
                ) : noCustomResults ? (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      height: '60vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Stack alignItems="center" spacing={2}>
                      <Box
                        component="img"
                        src={NoResultsFound}
                        alt=""
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'contain',
                        }}
                      />
                      <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#1E2A39' }}>
                        No result found
                      </Typography>

                      <Typography sx={{ fontSize: 13, color: '#718096' }}>
                        We can’t find the information you’re looking for
                      </Typography>
                    </Stack>
                  </Box>
                ) : null}
              </>
            ) : isWorkbenchInitialLoad ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 120,
                }}
              >
                <CircularProgress size={28} sx={{ color: '#1e2a39' }} />
              </Box>
            ) : (
              <>
                {workbenchProjects.map((project, projectIndex) => (
                  <Box
                    key={project.projectId}
                    component={motion.div}
                    initial={false}
                    animate={{
                      opacity: cardsEnter ? 1 : 0,
                      y: cardsEnter ? 0 : 14,
                    }}
                    transition={cardMotionTransition(projectIndex, cardsEnter)}
                  >
                    <WorkbenchProjectCard
                      imageSrc={getProjectPreviewImage([
                        project.projectId,
                        project.projectCode,
                        project.companyName,
                      ])}
                      project={project}
                      onEnterWorkspace={() =>
                        navigate(
                          {
                            pathname: `/projects/${project.projectId}/ffne`,
                            search: new URLSearchParams({ type: 'workbench' }).toString(),
                          },
                          {
                            state: buildHeaderNavigationState({
                              projectId: project.projectId,
                              projectName: getWorkbenchProjectDisplayName(
                                project.companyName,
                                project.projectCode,
                              ),
                            }),
                          },
                        )
                      }
                    />
                  </Box>
                ))}
                {noWorkbenchResults ? (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      height: '60vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Stack alignItems="center" spacing={2}>
                      <Box
                        component="img"
                        src={NoResultsFound}
                        alt=""
                        sx={{
                          width: 48,
                          height: 48,
                          objectFit: 'contain',
                        }}
                      />
                      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
                        No result found
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: '#718096' }}>
                        We can’t find the information you’re looking for
                      </Typography>
                    </Stack>
                  </Box>
                ) : null}
              </>
            )}
          </Box>
        </Stack>

        {tab === 'custom' && !isInitialLoad && customTotalCount > 0 ? (
          <HomePaginationBar
            page={page}
            totalCount={customTotalCount}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        ) : null}
        {tab === 'workbench' && !isWorkbenchInitialLoad && workbenchTotalCount > 0 ? (
          <HomePaginationBar
            page={page}
            totalCount={workbenchTotalCount}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        ) : null}
      </Stack>

      <CreateNewProjectDialog
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
      />
    </Box>
  );
}
